import { lazy, Suspense, useState, type ReactNode } from "react";
import { Toaster, SonnerToaster, TooltipProvider } from "@cieda/djayma-ui";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { handleQueryError } from "@/lib/api-error-handler";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";
import ErrorBoundary from "@/components/ErrorBoundary";
import AdminGuard from "@/components/auth/AdminGuard";
import AdminModuleProvider from "@/components/admin/AdminModuleProvider";
import { ModuleGuard } from "@/components/admin/modules/ModuleGuard";

// ---------------------------------------------------------------------------
// Gated route wrapper — wraps a page with auth + module provider + guard
// so hidden modules render the "Module desactive" placeholder instead of
// a broken page. Used for routes whose module flag is currently false.
// ---------------------------------------------------------------------------

function GatedRoute({ moduleKey, children }: { readonly moduleKey: string; readonly children: ReactNode }) {
  return (
    <AdminGuard>
      <AdminModuleProvider>
        <ModuleGuard moduleKey={moduleKey}>{children}</ModuleGuard>
      </AdminModuleProvider>
    </AdminGuard>
  );
}

// ---------------------------------------------------------------------------
// Admin pages — all lazy-loaded from djayma source via @ alias
// ---------------------------------------------------------------------------
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminOrders = lazy(() => import("@/pages/admin/OrderManagement"));
const AdminClients = lazy(() => import("@/pages/admin/ClientsCRM"));
const AdminFinances = lazy(() => import("@/pages/admin/FinanceDashboard"));
const AdminLogistics = lazy(() => import("@/pages/admin/LogisticsManagement"));
const AdminReturns = lazy(() => import("@/pages/admin/ReturnsManagement"));
const AdminInvoices = lazy(() => import("@/pages/admin/InvoiceManagement"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminOrderDetail = lazy(() => import("@/pages/admin/OrderDetail"));

// DJAYMA B2B engine — web app (Epic 5): control tower, finance, call center
const AdminDjaymaControlTower = lazy(() => import("@/pages/admin/djayma/ControlTower"));
const AdminDjaymaFinance = lazy(() => import("@/pages/admin/djayma/FinanceReconciliation"));
const AdminDjaymaCallCenter = lazy(() => import("@/pages/admin/djayma/CallCenter"));
const AdminDjaymaStock = lazy(() => import("@/pages/admin/djayma/StockConsole"));
const AdminDjaymaOrders = lazy(() => import("@/pages/admin/djayma/OrdersBoard"));
const AdminDjaymaLogistique = lazy(() => import("@/pages/admin/djayma/LogistiqueConsole"));
const AdminDjaymaPricing = lazy(() => import("@/pages/admin/djayma/PriceBook"));
const AdminDjaymaPromos = lazy(() => import("@/pages/admin/djayma/PromoBoard"));
const AdminDjaymaPartners = lazy(() => import("@/pages/admin/djayma/PartnersBoard"));
const AdminDjaymaChannels = lazy(() => import("@/pages/admin/djayma/ChannelMix"));
const AdminDjaymaAccess = lazy(() => import("@/pages/admin/djayma/AccessDirectory"));

// Commerce
const AdminCommerceOrders = lazy(() => import("@/pages/admin/commerce/Orders"));
const AdminProducts = lazy(() => import("@/pages/admin/commerce/Products"));
const AdminCategories = lazy(() => import("@/pages/admin/commerce/Categories"));
const AdminInventory = lazy(() => import("@/pages/admin/commerce/Inventory"));
const AdminStockAlerts = lazy(() => import("@/pages/admin/commerce/StockAlerts"));
const AdminNotifications = lazy(() => import("@/pages/admin/commerce/Notifications"));

// Payments
const AdminPayments = lazy(() => import("@/pages/admin/payments/PaymentManager"));
const AdminReconciliation = lazy(() => import("@/pages/admin/payments/Reconciliation"));
const AdminRefunds = lazy(() => import("@/pages/admin/payments/Refunds"));
const AdminDisputes = lazy(() => import("@/pages/admin/payments/Disputes"));
const AdminDailyCash = lazy(() => import("@/pages/admin/payments/DailyCash"));
const AdminCallbackLogs = lazy(() => import("@/pages/admin/payments/CallbackLogs"));
const AdminDeadLetterQueue = lazy(() => import("@/pages/admin/payments/DeadLetterQueue"));

// Delivery
const AdminDeliveries = lazy(() => import("@/pages/admin/delivery/DeliveryBoard"));
const AdminDrivers = lazy(() => import("@/pages/admin/delivery/Drivers"));
const AdminRoutes = lazy(() => import("@/pages/admin/delivery/Routes"));
const AdminTracking = lazy(() => import("@/pages/admin/delivery/LiveTracking"));
const AdminYango = lazy(() => import("@/pages/admin/delivery/Yango"));
const AdminDeliveryZones = lazy(() => import("@/pages/admin/delivery/DeliveryZones"));
const AdminDeliverySlots = lazy(() => import("@/pages/admin/delivery/DeliverySlots"));
const AdminDepots = lazy(() => import("@/pages/admin/delivery/Depots"));
const AdminDeliveryPerformance = lazy(() => import("@/pages/admin/delivery/DeliveryPerformance"));

// Yoboulmako — extractable section (remove these 5 lines when Yoboulmako stands alone)
const YoboulmakoDashboard      = lazy(() => import("@/pages/yoboulmako/Dashboard"));
const YoboulmakoLiveDeliveries = lazy(() => import("@/pages/yoboulmako/LiveDeliveries"));
const YoboulmakoFeatureFlags   = lazy(() => import("@/pages/yoboulmako/FeatureFlags"));
const YoboulmakoBilling        = lazy(() => import("@/pages/yoboulmako/Billing"));
const YoboulmakoClients        = lazy(() => import("@/pages/yoboulmako/Clients"));
const YoboulmakoCodReconciliation = lazy(() => import("@/pages/yoboulmako/CodReconciliation"));

// Yoboulmako Dispatcher Console
const DispatchList   = lazy(() => import("@/pages/yoboulmako/dispatcher/DispatchList"));
const DispatchDetail = lazy(() => import("@/pages/yoboulmako/dispatcher/DispatchDetail"));
const DispatchMap    = lazy(() => import("@/pages/yoboulmako/dispatcher/DispatchMap"));

// Launch planning (J-Day)
const AdminLaunchDashboard = lazy(() => import("@/pages/admin/LaunchDashboard"));

// B2B
const AdminB2BCustomers = lazy(() => import("@/pages/admin/b2b/Customers"));
const AdminB2BPriceLists = lazy(() => import("@/pages/admin/b2b/PriceLists"));
const AdminB2BCredit = lazy(() => import("@/pages/admin/b2b/Credit"));
const AdminB2BApprovals = lazy(() => import("@/pages/admin/b2b/Approvals"));
const AdminB2BImport = lazy(() => import("@/pages/admin/b2b/Import"));
const AdminB2BInvoices = lazy(() => import("@/pages/admin/b2b/Invoices"));
const AdminB2BAging = lazy(() => import("@/pages/admin/b2b/Aging"));
const AdminSuppliers = lazy(() => import("@/pages/admin/b2b/Suppliers"));
const AdminB2BVolumeDiscounts = lazy(() => import("@/pages/admin/b2b/VolumeDiscounts"));
const AdminB2BReps = lazy(() => import("@/pages/admin/b2b/Reps"));

// CRM
const AdminSegments = lazy(() => import("@/pages/admin/crm/Segments"));
const AdminLoyalty = lazy(() => import("@/pages/admin/crm/Loyalty"));
const AdminWhatsApp = lazy(() => import("@/pages/admin/crm/WhatsApp"));
const AdminSMS = lazy(() => import("@/pages/admin/crm/SMS"));
const AdminFeedback = lazy(() => import("@/pages/admin/crm/Feedback"));
const AdminAbandonedCarts = lazy(() => import("@/pages/admin/crm/AbandonedCarts"));
const AdminCLV = lazy(() => import("@/pages/admin/crm/CLV"));
const AdminReferrals = lazy(() => import("@/pages/admin/crm/Referrals"));
const AdminCommunications = lazy(() => import("@/pages/admin/crm/Communications"));

// Marketing
const AdminPromotions = lazy(() => import("@/pages/admin/marketing/Promotions"));
const AdminCoupons = lazy(() => import("@/pages/admin/marketing/Coupons"));
const AdminFlashSales = lazy(() => import("@/pages/admin/marketing/FlashSales"));
const AdminBundles = lazy(() => import("@/pages/admin/marketing/Bundles"));
const AdminBanners = lazy(() => import("@/pages/admin/marketing/Banners"));
const AdminNewsletter = lazy(() => import("@/pages/admin/marketing/Newsletter"));
const AdminSeasonal = lazy(() => import("@/pages/admin/marketing/Seasonal"));
const AdminStackingRules = lazy(() => import("@/pages/admin/marketing/StackingRules"));
const AdminPromoAnalytics = lazy(() => import("@/pages/admin/marketing/PromoAnalytics"));
const AdminTrackingLinks = lazy(() => import("@/pages/admin/marketing/TrackingLinks"));

// Finance
const AdminRevenue = lazy(() => import("@/pages/admin/finance/Revenue"));
const AdminTVA = lazy(() => import("@/pages/admin/finance/TVA"));
const AdminMargins = lazy(() => import("@/pages/admin/finance/Margins"));
const AdminExpenses = lazy(() => import("@/pages/admin/finance/Expenses"));
const AdminFinanceInvoices = lazy(() => import("@/pages/admin/finance/Invoices"));
const AdminCashflow = lazy(() => import("@/pages/admin/finance/Cashflow"));
const AdminSageExport = lazy(() => import("@/pages/admin/finance/SageExport"));
const AdminPowerBI = lazy(() => import("@/pages/admin/finance/PowerBI"));
const AdminReports = lazy(() => import("@/pages/admin/finance/Reports"));
const AdminDailyPnL = lazy(() => import("@/pages/admin/finance/DailyPnL"));

// Operations
const AdminAdminUsers = lazy(() => import("@/pages/admin/operations/AdminUsers"));
const AdminRoles = lazy(() => import("@/pages/admin/operations/Roles"));
const AdminAuditLog = lazy(() => import("@/pages/admin/operations/AuditLog"));
const AdminTasks = lazy(() => import("@/pages/admin/operations/Tasks"));
const AdminShifts = lazy(() => import("@/pages/admin/operations/Shifts"));
const AdminAgentPerformance = lazy(() => import("@/pages/admin/operations/AgentPerformance"));
const AdminAnnouncements = lazy(() => import("@/pages/admin/operations/Announcements"));
const AdminSLA = lazy(() => import("@/pages/admin/operations/SLA"));
const AdminIncidents = lazy(() => import("@/pages/admin/operations/Incidents"));
const AdminMessages = lazy(() => import("@/pages/admin/operations/Messages"));

// AI
const AdminAIDashboard = lazy(() => import("@/pages/admin/ai/Dashboard"));
const AdminDemandForecast = lazy(() => import("@/pages/admin/ai/DemandForecast"));
const AdminSmartPricing = lazy(() => import("@/pages/admin/ai/SmartPricing"));
const AdminChurnPrediction = lazy(() => import("@/pages/admin/ai/ChurnPrediction"));
const AdminAnomalyDetection = lazy(() => import("@/pages/admin/ai/AnomalyDetection"));
const AdminReplenishment = lazy(() => import("@/pages/admin/ai/Replenishment"));
const AdminSalesInsights = lazy(() => import("@/pages/admin/ai/SalesInsights"));
const AdminRecommendations = lazy(() => import("@/pages/admin/ai/Recommendations"));
const AdminSentimentAnalysis = lazy(() => import("@/pages/admin/ai/SentimentAnalysis"));
const AdminModelMonitor = lazy(() => import("@/pages/admin/ai/ModelMonitor"));

// Settings
const ModuleSettings = lazy(() => import("@/pages/admin/settings/ModuleSettings"));

// Analytics (sidebar French routes)
const AdminAnalyticsOverview = lazy(() => import("@/pages/admin/analytics/Overview"));
const AdminAnalyticsTrending = lazy(() => import("@/pages/admin/analytics/Trending"));
const AdminAnalyticsSearch = lazy(() => import("@/pages/admin/analytics/SearchAnalytics"));
const AdminAnalyticsFunnel = lazy(() => import("@/pages/admin/analytics/Funnel"));
const AdminAnalyticsIntent = lazy(() => import("@/pages/admin/analytics/Intent"));

// Hub pages (sidebar French routes)
const AdminContentManager = lazy(() => import("@/pages/admin/content/ContentManager"));
const AdminOperationsHub = lazy(() => import("@/pages/admin/operations/OperationsHub"));
const AdminAccountDeletions = lazy(() => import("@/pages/admin/operations/AccountDeletions"));
const AdminPrivacy = lazy(() => import("@/pages/admin/privacy/Privacy"));
const AdminEnterpriseHub = lazy(() => import("@/pages/admin/enterprise/EnterpriseHub"));

// Enterprise
const AdminGDPR = lazy(() => import("@/pages/admin/enterprise/GDPR"));
const AdminEInvoice = lazy(() => import("@/pages/admin/enterprise/EInvoice"));
const AdminBackup = lazy(() => import("@/pages/admin/enterprise/Backup"));
const AdminEnterpriseApiKeys = lazy(() => import("@/pages/admin/enterprise/ApiKeys"));
const AdminWebhooks = lazy(() => import("@/pages/admin/enterprise/Webhooks"));
const AdminSystemHealth = lazy(() => import("@/pages/admin/enterprise/SystemHealth"));
const AdminPerformance = lazy(() => import("@/pages/admin/enterprise/Performance"));
const AdminFeatureFlags = lazy(() => import("@/pages/admin/enterprise/FeatureFlags"));
const AdminDataExport = lazy(() => import("@/pages/admin/enterprise/DataExport"));
const AdminIntegrations = lazy(() => import("@/pages/admin/enterprise/Integrations"));

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-secondary border-t-transparent" />
  </div>
);

function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({ onError: handleQueryError }),
    mutationCache: new MutationCache({ onError: handleQueryError }),
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 10 * 60_000,
        retry: (failureCount, error) => {
          if (error && (error as { name?: string }).name === "RateLimitError") return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
    },
  });
}

const App = () => {
  const [queryClient] = useState(createQueryClient);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <SonnerToaster />
          <BrowserRouter>
            {/*
              NuqsAdapter MUST sit inside BrowserRouter so nuqs hooks
              (useQueryState / useQueryStates used by Orders, Products, etc.)
              can read+write the URL via react-router. Without this, every
              page that uses nuqs throws the "nuqs requires an adapter"
              error and the ErrorBoundary shows the 500 page.
            */}
            <NuqsAdapter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Root redirects to admin login */}
                <Route path="/" element={<Navigate to="/admin/connexion" replace />} />

                {/* Admin login — public */}
                <Route path="/admin/connexion" element={<AdminLogin />} />

                {/* Dashboard */}
                <Route path="/admin" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
                <Route path="/admin/djayma" element={<AdminGuard><AdminDjaymaControlTower /></AdminGuard>} />
                <Route path="/admin/djayma/finance" element={<AdminGuard><AdminDjaymaFinance /></AdminGuard>} />
                <Route path="/admin/djayma/call-center" element={<AdminGuard><AdminDjaymaCallCenter /></AdminGuard>} />
                <Route path="/admin/djayma/stock" element={<AdminGuard><AdminDjaymaStock /></AdminGuard>} />
                <Route path="/admin/djayma/commandes" element={<AdminGuard><AdminDjaymaOrders /></AdminGuard>} />
                <Route path="/admin/djayma/logistique" element={<AdminGuard><AdminDjaymaLogistique /></AdminGuard>} />
                <Route path="/admin/djayma/prix" element={<AdminGuard><AdminDjaymaPricing /></AdminGuard>} />
                <Route path="/admin/djayma/promotions" element={<AdminGuard><AdminDjaymaPromos /></AdminGuard>} />
                <Route path="/admin/djayma/partenaires" element={<AdminGuard><AdminDjaymaPartners /></AdminGuard>} />
                <Route path="/admin/djayma/canaux" element={<AdminGuard><AdminDjaymaChannels /></AdminGuard>} />
                <Route path="/admin/djayma/acces" element={<AdminGuard><AdminDjaymaAccess /></AdminGuard>} />

                {/* Legacy French routes */}
                <Route path="/admin/clients" element={<AdminGuard><AdminClients /></AdminGuard>} />
                <Route path="/admin/commandes" element={<AdminGuard><AdminOrders /></AdminGuard>} />
                <Route path="/admin/finances" element={<AdminGuard><AdminFinances /></AdminGuard>} />
                <Route path="/admin/logistique" element={<GatedRoute moduleKey="logistics"><AdminLogistics /></GatedRoute>} />
                <Route path="/admin/retours" element={<AdminGuard><AdminReturns /></AdminGuard>} />
                <Route path="/admin/factures" element={<AdminGuard><AdminInvoices /></AdminGuard>} />
                <Route path="/admin/parametres" element={<AdminGuard><AdminSettings /></AdminGuard>} />

                {/* Commerce */}
                <Route path="/admin/orders/:id" element={<AdminGuard><AdminOrderDetail /></AdminGuard>} />
                <Route path="/admin/orders" element={<AdminGuard><AdminCommerceOrders /></AdminGuard>} />
                <Route path="/admin/products" element={<AdminGuard><AdminProducts /></AdminGuard>} />
                <Route path="/admin/categories" element={<AdminGuard><AdminCategories /></AdminGuard>} />
                <Route path="/admin/inventory" element={<AdminGuard><AdminInventory /></AdminGuard>} />
                <Route path="/admin/stock-alerts" element={<AdminGuard><AdminStockAlerts /></AdminGuard>} />
                <Route path="/admin/notifications" element={<AdminGuard><AdminNotifications /></AdminGuard>} />

                {/* Payments */}
                <Route path="/admin/payments" element={<AdminGuard><AdminPayments /></AdminGuard>} />
                <Route path="/admin/reconciliation" element={<AdminGuard><AdminModuleProvider><AdminReconciliation /></AdminModuleProvider></AdminGuard>} />
                <Route path="/admin/refunds" element={<AdminGuard><AdminModuleProvider><AdminRefunds /></AdminModuleProvider></AdminGuard>} />
                <Route path="/admin/disputes" element={<AdminGuard><AdminDisputes /></AdminGuard>} />
                <Route path="/admin/daily-cash" element={<AdminGuard><AdminModuleProvider><AdminDailyCash /></AdminModuleProvider></AdminGuard>} />
                <Route path="/admin/payments/callbacks" element={<AdminGuard><AdminModuleProvider><AdminCallbackLogs /></AdminModuleProvider></AdminGuard>} />
                <Route path="/admin/payments/dead-letter" element={<AdminGuard><AdminModuleProvider><AdminDeadLetterQueue /></AdminModuleProvider></AdminGuard>} />

                {/* Delivery — gated on logistics module flag */}
                <Route path="/admin/deliveries" element={<GatedRoute moduleKey="logistics"><AdminDeliveries /></GatedRoute>} />
                <Route path="/admin/drivers" element={<GatedRoute moduleKey="logistics"><AdminDrivers /></GatedRoute>} />
                <Route path="/admin/routes" element={<GatedRoute moduleKey="logistics"><AdminRoutes /></GatedRoute>} />
                <Route path="/admin/tracking" element={<GatedRoute moduleKey="logistics"><AdminTracking /></GatedRoute>} />
                <Route path="/admin/yango" element={<GatedRoute moduleKey="logistics"><AdminYango /></GatedRoute>} />
                <Route path="/admin/delivery-zones" element={<GatedRoute moduleKey="logistics"><AdminDeliveryZones /></GatedRoute>} />
                <Route path="/admin/delivery-slots" element={<GatedRoute moduleKey="logistics"><AdminDeliverySlots /></GatedRoute>} />
                <Route path="/admin/depots" element={<GatedRoute moduleKey="logistics"><AdminDepots /></GatedRoute>} />
                <Route path="/admin/delivery-performance" element={<GatedRoute moduleKey="logistics"><AdminDeliveryPerformance /></GatedRoute>} />

                {/* ── Yoboulmako (extractable — remove this block when Yoboulmako stands alone) ── */}
                <Route path="/admin/yoboulmako" element={<AdminGuard><AdminModuleProvider><YoboulmakoDashboard /></AdminModuleProvider></AdminGuard>} />
                <Route path="/admin/yoboulmako/dashboard" element={<AdminGuard><AdminModuleProvider><YoboulmakoDashboard /></AdminModuleProvider></AdminGuard>} />
                <Route path="/admin/yoboulmako/livraisons" element={<AdminGuard><AdminModuleProvider><YoboulmakoLiveDeliveries /></AdminModuleProvider></AdminGuard>} />
                <Route path="/admin/yoboulmako/parametres" element={<AdminGuard><AdminModuleProvider><YoboulmakoFeatureFlags /></AdminModuleProvider></AdminGuard>} />
                <Route path="/admin/yoboulmako/facturation" element={<AdminGuard><AdminModuleProvider><YoboulmakoBilling /></AdminModuleProvider></AdminGuard>} />
                <Route path="/admin/yoboulmako/clients" element={<AdminGuard><AdminModuleProvider><YoboulmakoClients /></AdminModuleProvider></AdminGuard>} />
                <Route path="/admin/yoboulmako/cod" element={<AdminGuard><AdminModuleProvider><YoboulmakoCodReconciliation /></AdminModuleProvider></AdminGuard>} />

                {/* Dispatcher Console — map MUST come before :ref to avoid swallowing "map" as a ref param */}
                <Route path="/admin/yoboulmako/dispatches" element={<AdminGuard><AdminModuleProvider><DispatchList /></AdminModuleProvider></AdminGuard>} />
                <Route path="/admin/yoboulmako/dispatches/map" element={<AdminGuard><AdminModuleProvider><DispatchMap /></AdminModuleProvider></AdminGuard>} />
                <Route path="/admin/yoboulmako/dispatches/:ref" element={<AdminGuard><AdminModuleProvider><DispatchDetail /></AdminModuleProvider></AdminGuard>} />

                {/* Launch planning (J-Day) */}
                <Route path="/admin/lancement" element={<AdminGuard><AdminLaunchDashboard /></AdminGuard>} />

                {/* B2B */}
                <Route path="/admin/b2b/customers" element={<AdminGuard><AdminB2BCustomers /></AdminGuard>} />
                <Route path="/admin/b2b/price-lists" element={<AdminGuard><AdminB2BPriceLists /></AdminGuard>} />
                <Route path="/admin/b2b/credit" element={<AdminGuard><AdminB2BCredit /></AdminGuard>} />
                <Route path="/admin/b2b/approvals" element={<AdminGuard><AdminB2BApprovals /></AdminGuard>} />
                <Route path="/admin/b2b/import" element={<AdminGuard><AdminB2BImport /></AdminGuard>} />
                <Route path="/admin/b2b/invoices" element={<AdminGuard><AdminB2BInvoices /></AdminGuard>} />
                <Route path="/admin/b2b/aging" element={<AdminGuard><AdminB2BAging /></AdminGuard>} />
                <Route path="/admin/suppliers" element={<AdminGuard><AdminSuppliers /></AdminGuard>} />
                <Route path="/admin/b2b/volume-discounts" element={<AdminGuard><AdminB2BVolumeDiscounts /></AdminGuard>} />
                <Route path="/admin/b2b/reps" element={<AdminGuard><AdminB2BReps /></AdminGuard>} />

                {/* CRM — gated on crm module flag */}
                <Route path="/admin/segments" element={<GatedRoute moduleKey="crm"><AdminSegments /></GatedRoute>} />
                <Route path="/admin/loyalty" element={<GatedRoute moduleKey="crm"><AdminLoyalty /></GatedRoute>} />
                <Route path="/admin/whatsapp" element={<GatedRoute moduleKey="crm"><AdminWhatsApp /></GatedRoute>} />
                <Route path="/admin/sms" element={<GatedRoute moduleKey="crm"><AdminSMS /></GatedRoute>} />
                <Route path="/admin/feedback" element={<GatedRoute moduleKey="crm"><AdminFeedback /></GatedRoute>} />
                <Route path="/admin/abandoned-carts" element={<GatedRoute moduleKey="crm"><AdminAbandonedCarts /></GatedRoute>} />
                <Route path="/admin/clv" element={<GatedRoute moduleKey="crm"><AdminCLV /></GatedRoute>} />
                <Route path="/admin/referrals" element={<GatedRoute moduleKey="crm"><AdminReferrals /></GatedRoute>} />
                <Route path="/admin/communications" element={<GatedRoute moduleKey="crm"><AdminCommunications /></GatedRoute>} />

                {/* Marketing */}
                <Route path="/admin/promotions" element={<AdminGuard><AdminPromotions /></AdminGuard>} />
                <Route path="/admin/coupons" element={<AdminGuard><AdminCoupons /></AdminGuard>} />
                <Route path="/admin/flash-sales" element={<AdminGuard><AdminFlashSales /></AdminGuard>} />
                <Route path="/admin/bundles" element={<AdminGuard><AdminBundles /></AdminGuard>} />
                <Route path="/admin/banners" element={<AdminGuard><AdminBanners /></AdminGuard>} />
                <Route path="/admin/newsletter" element={<AdminGuard><AdminNewsletter /></AdminGuard>} />
                <Route path="/admin/seasonal" element={<AdminGuard><AdminSeasonal /></AdminGuard>} />
                <Route path="/admin/stacking-rules" element={<AdminGuard><AdminStackingRules /></AdminGuard>} />
                <Route path="/admin/promo-analytics" element={<AdminGuard><AdminPromoAnalytics /></AdminGuard>} />
                <Route path="/admin/tracking-links" element={<AdminGuard><AdminTrackingLinks /></AdminGuard>} />

                {/* Finance — gated on finance-dashboard module flag */}
                <Route path="/admin/revenue" element={<GatedRoute moduleKey="finance-dashboard"><AdminRevenue /></GatedRoute>} />
                <Route path="/admin/tva" element={<GatedRoute moduleKey="finance-dashboard"><AdminTVA /></GatedRoute>} />
                <Route path="/admin/margins" element={<GatedRoute moduleKey="finance-dashboard"><AdminMargins /></GatedRoute>} />
                <Route path="/admin/expenses" element={<GatedRoute moduleKey="finance-dashboard"><AdminExpenses /></GatedRoute>} />
                <Route path="/admin/invoices" element={<GatedRoute moduleKey="finance-dashboard"><AdminFinanceInvoices /></GatedRoute>} />
                <Route path="/admin/cashflow" element={<GatedRoute moduleKey="finance-dashboard"><AdminCashflow /></GatedRoute>} />
                <Route path="/admin/sage" element={<GatedRoute moduleKey="sage-erp"><AdminSageExport /></GatedRoute>} />
                <Route path="/admin/powerbi" element={<GatedRoute moduleKey="finance-dashboard"><AdminPowerBI /></GatedRoute>} />
                <Route path="/admin/reports" element={<GatedRoute moduleKey="finance-dashboard"><AdminReports /></GatedRoute>} />
                <Route path="/admin/daily-pnl" element={<GatedRoute moduleKey="finance-dashboard"><AdminDailyPnL /></GatedRoute>} />

                {/* Operations */}
                <Route path="/admin/admin-users" element={<AdminGuard><AdminAdminUsers /></AdminGuard>} />
                <Route path="/admin/roles" element={<AdminGuard><AdminRoles /></AdminGuard>} />
                <Route path="/admin/audit-log" element={<AdminGuard><AdminAuditLog /></AdminGuard>} />
                <Route path="/admin/tasks" element={<AdminGuard><AdminTasks /></AdminGuard>} />
                <Route path="/admin/shifts" element={<AdminGuard><AdminShifts /></AdminGuard>} />
                <Route path="/admin/agent-performance" element={<AdminGuard><AdminAgentPerformance /></AdminGuard>} />
                <Route path="/admin/announcements" element={<AdminGuard><AdminAnnouncements /></AdminGuard>} />
                <Route path="/admin/sla" element={<AdminGuard><AdminSLA /></AdminGuard>} />
                <Route path="/admin/incidents" element={<AdminGuard><AdminIncidents /></AdminGuard>} />
                <Route path="/admin/messages" element={<AdminGuard><AdminMessages /></AdminGuard>} />

                {/* Enterprise */}
                <Route path="/admin/gdpr" element={<AdminGuard><AdminGDPR /></AdminGuard>} />
                <Route path="/admin/einvoice" element={<AdminGuard><AdminEInvoice /></AdminGuard>} />
                <Route path="/admin/backup" element={<AdminGuard><AdminBackup /></AdminGuard>} />
                <Route path="/admin/api-keys" element={<AdminGuard><AdminEnterpriseApiKeys /></AdminGuard>} />
                <Route path="/admin/webhooks" element={<AdminGuard><AdminWebhooks /></AdminGuard>} />
                <Route path="/admin/system-health" element={<AdminGuard><AdminSystemHealth /></AdminGuard>} />
                <Route path="/admin/performance" element={<AdminGuard><AdminPerformance /></AdminGuard>} />
                <Route path="/admin/feature-flags" element={<AdminGuard><AdminFeatureFlags /></AdminGuard>} />
                <Route path="/admin/data-export" element={<AdminGuard><AdminDataExport /></AdminGuard>} />
                <Route path="/admin/integrations" element={<AdminGuard><AdminIntegrations /></AdminGuard>} />

                {/* AI */}
                <Route path="/admin/ai" element={<AdminGuard><AdminAIDashboard /></AdminGuard>} />
                <Route path="/admin/ai/forecast" element={<AdminGuard><AdminDemandForecast /></AdminGuard>} />
                <Route path="/admin/ai/smart-pricing" element={<AdminGuard><AdminSmartPricing /></AdminGuard>} />
                <Route path="/admin/ai/churn" element={<AdminGuard><AdminChurnPrediction /></AdminGuard>} />
                <Route path="/admin/ai/anomalies" element={<AdminGuard><AdminAnomalyDetection /></AdminGuard>} />
                <Route path="/admin/ai/replenishment" element={<AdminGuard><AdminReplenishment /></AdminGuard>} />
                <Route path="/admin/ai/insights" element={<AdminGuard><AdminSalesInsights /></AdminGuard>} />
                <Route path="/admin/ai/recommendations" element={<AdminGuard><AdminRecommendations /></AdminGuard>} />
                <Route path="/admin/ai/sentiment" element={<AdminGuard><AdminSentimentAnalysis /></AdminGuard>} />
                <Route path="/admin/ai/models" element={<AdminGuard><AdminModelMonitor /></AdminGuard>} />

                {/* Settings */}
                <Route path="/admin/modules" element={<AdminGuard><AdminModuleProvider><ModuleSettings /></AdminModuleProvider></AdminGuard>} />

                {/* Analytics (sidebar French routes) */}
                <Route path="/admin/analytics" element={<AdminGuard><AdminAnalyticsOverview /></AdminGuard>} />
                <Route path="/admin/analytics/trending" element={<AdminGuard><AdminAnalyticsTrending /></AdminGuard>} />
                <Route path="/admin/analytics/search" element={<AdminGuard><AdminAnalyticsSearch /></AdminGuard>} />
                <Route path="/admin/analytics/funnel" element={<AdminGuard><AdminAnalyticsFunnel /></AdminGuard>} />
                <Route path="/admin/analytics/intent" element={<AdminGuard><AdminAnalyticsIntent /></AdminGuard>} />

                {/* Hub pages (sidebar French routes) */}
                <Route path="/admin/contenu" element={<AdminGuard><AdminContentManager /></AdminGuard>} />
                <Route path="/admin/operations" element={<AdminGuard><AdminOperationsHub /></AdminGuard>} />
                <Route path="/admin/operations/account-deletions" element={<AdminGuard><AdminAccountDeletions /></AdminGuard>} />
                <Route path="/admin/vie-privee" element={<AdminGuard><AdminPrivacy /></AdminGuard>} />
                <Route path="/admin/entreprise" element={<AdminGuard><AdminEnterpriseHub /></AdminGuard>} />
                <Route path="/admin/delivery/flags" element={<AdminGuard><AdminFeatureFlags /></AdminGuard>} />

                {/* Catch-all — redirect to admin login */}
                <Route path="*" element={<Navigate to="/admin/connexion" replace />} />
              </Routes>
            </Suspense>
            </NuqsAdapter>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
